import { defineInternalTask } from '../../../config/types';
import { internalTasks, reviewTeams } from '../../../config/constants';

import { baseBindState, getPrimarySegment } from './lib/helpers';

export const illogicalCombinationReview = defineInternalTask({
  taskName: internalTasks.IllogicalCombinationReview,
  config: {
    staffDisplayName: 'Atypical home details: review needed',
    reviewTeam: reviewTeams.uwOps,
    policyTypes: ['H'],
    enabled: true,
    trigger: ({ policy }) => {
      const segment = getPrimarySegment(policy);
      const home = segment?.home;
      if (!home) {
        return null;
      }

      const flags: string[] = [];
      const sqFt = home.sqFt;
      const numStories = home.numStories;
      const numFullBathrooms = home.numFullBathrooms;
      const numHalfBathrooms = home.numHalfBathrooms;
      // Runtime field is `typeOfHome` (schema-types appsync.d.ts:1897) — an enum:
      // '1' = Single Home, '3' = Town Home, '4' = Other. Earlier revision compared
      // `homeType === 'O'` / `'S'` against this field — wrong field name AND wrong
      // enum values — so none of these flags had ever fired.
      const typeOfHome = home.typeOfHome;
      const roofShape = home.roofShape;
      const roofType = home.roofType;

      if (typeOfHome === '4') {
        flags.push('Home Type of Other');
      }
      if (sqFt && sqFt < 750) {
        flags.push('Above ground square footage Under 750 sq ft');
      }
      if (numStories && numStories >= 2 && sqFt && sqFt < 1000) {
        // Label says "2+ stories" (not "two story") because the condition fires on any
        // numStories >= 2 — a 3-story home should not be labeled "two story".
        flags.push('Multi-story home (2+ stories) under 1000 sq ft above ground');
      }
      // Only flag low bathroom count when at least one bathroom field is actually
      // populated — otherwise a brand-new policy with missing data trips this every time.
      const hasBathroomData = numFullBathrooms != null || numHalfBathrooms != null;
      if (hasBathroomData) {
        const totalBathrooms = (numFullBathrooms || 0) + (numHalfBathrooms || 0) * 0.5;
        if (totalBathrooms <= 1) {
          flags.push('Total bathrooms is <= 1');
        }
      }
      if (numStories && numStories >= 3 && typeOfHome === '1') {
        flags.push('Number of stories = 3 or greater when home type is Single Home');
      }
      if (roofShape && ['G', 'H'].includes(roofShape) && roofType === 'O') {
        flags.push('Roof shape is Gable or Hip and Roof Type is "other"');
      }

      if (flags.length === 0) {
        return null;
      }
      return { data: { flags }, bindState: baseBindState(policy) };
    },
    description: ({ data }) =>
      `Review the following home details for accuracy:
${(data.flags || []).join('\n')}`
  }
});
